/**
 * MarPrintPanel — Offline MAR generation and printing (DRR-303)
 *
 * Healthcare-specific component for medication administration records.
 * Generates self-contained HTML that can be printed for offline documentation
 * when internet connectivity is lost.
 *
 * ## Offline Workflow
 *
 * 1. At shift start, generate MAR HTML via generateOfflineMar mutation
 * 2. HTML is cached in Valkey (backend) and IndexedDB (frontend)
 * 3. When offline: display cached HTML, print for paper documentation
 * 4. When online: enter paper documentation into FERP
 *
 * DUMB FRONTEND: Backend generates complete HTML with inline styles.
 * This component just fetches, caches, and displays.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Stack,
  Text,
  Button,
  Group,
  Select,
  Alert,
  Paper,
  LoadingOverlay,
  Badge,
  Modal,
  ScrollArea,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconPrinter,
  IconDownload,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
  IconCloudOff,
} from '@tabler/icons-react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuth } from '@ferp/auth';

// ============================================================================
// GraphQL operations — Uses existing renderHtmlPdf mutation (DRY)
// ============================================================================

const RENDER_HTML_PDF = gql`
  mutation RenderHtmlPdf($input: RenderHtmlPdfInput!) {
    renderHtmlPdf(input: $input) {
      success
      downloadUrl
      templateId
      contentType
      generatedAt
      message
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

type MarShift = 'AM' | 'PM' | 'NOC';

interface RenderHtmlPdfInput {
  templateId: string;
  tenantId: string;
  variables: Record<string, unknown>;
  debugHtml: boolean;
}

interface RenderHtmlPdfResult {
  success: boolean;
  downloadUrl: string;
  templateId: string;
  contentType: string;
  generatedAt: string;
  message: string | null;
}

export interface MarPrintPanelProps {
  tenantId: string;
  date?: string;
  defaultShift?: MarShift;
  onPrint?: () => void;
}

// ============================================================================
// IndexedDB cache helpers
// ============================================================================

const DB_NAME = 'ferp-offline-mar';
const STORE_NAME = 'mar-cache';

async function openMarDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
      }
    };
  });
}

async function cacheMarLocally(cacheKey: string, html: string, expiresAt: string): Promise<void> {
  try {
    const db = await openMarDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ cacheKey, html, expiresAt, cachedAt: new Date().toISOString() });
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (err) {
    console.warn('Failed to cache MAR locally:', err);
  }
}

async function getLocalMarCache(cacheKey: string): Promise<{ html: string; expiresAt: string } | null> {
  try {
    const db = await openMarDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(cacheKey);
    const result = await new Promise<{ html: string; expiresAt: string } | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    db.close();

    if (result && new Date(result.expiresAt) > new Date()) {
      return result;
    }
    return null;
  } catch (err) {
    console.warn('Failed to read local MAR cache:', err);
    return null;
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function MarPrintPanel({
  tenantId,
  date = new Date().toISOString().split('T')[0],
  defaultShift,
  onPrint,
}: MarPrintPanelProps) {
  const { isAuthenticated } = useAuth();
  const [shift, setShift] = useState<MarShift>(defaultShift ?? getCurrentShift());
  const [marHtml, setMarHtml] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [cacheStatus, setCacheStatus] = useState<'none' | 'cached' | 'expired'>('none');

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // date has default in destructure, ?? provides additional safety for TS
  const currentDate = (date ?? new Date().toISOString().split('T')[0]) as string;

  // Generate MAR mutation — uses existing renderHtmlPdf (DRY)
  const [renderPdf, { loading: generating }] = useMutation<
    { renderHtmlPdf: RenderHtmlPdfResult },
    { input: RenderHtmlPdfInput }
  >(RENDER_HTML_PDF);

  // Check local cache on mount and shift change
  useEffect(() => {
    const cacheKey = `mar:cache:${tenantId}:${currentDate}:${shift}`;
    getLocalMarCache(cacheKey).then((cached) => {
      if (cached) {
        setCacheStatus('cached');
        setMarHtml(cached.html);
      } else {
        setCacheStatus('none');
        setMarHtml(null);
      }
    });
  }, [tenantId, currentDate, shift]);

  // Generate MAR handler — uses existing renderHtmlPdf mutation
  const handleGenerateMar = useCallback(async () => {
    try {
      const result = await renderPdf({
        variables: {
          input: {
            templateId: 'hc-mar-daily',
            tenantId,
            variables: {
              mar_date: currentDate,
              shift_label: shift === 'AM' ? 'Morning (6am - 2pm)' :
                          shift === 'PM' ? 'Afternoon (2pm - 10pm)' :
                          'Night (10pm - 6am)',
            },
            debugHtml: true, // Returns HTML instead of PDF
          },
        },
      });

      const data = result.data?.renderHtmlPdf;
      if (data?.success && data?.downloadUrl) {
        // Fetch HTML from the download URL
        const response = await fetch(data.downloadUrl);
        const html = await response.text();

        setMarHtml(html);
        setCacheStatus('cached');

        // Cache locally in IndexedDB (24 hour expiry)
        const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        const cacheKey = `mar:cache:${tenantId}:${currentDate}:${shift}`;
        await cacheMarLocally(cacheKey, html, expiresAt);
      }
    } catch (err) {
      console.error('Failed to generate MAR:', err);
    }
  }, [renderPdf, tenantId, currentDate, shift]);

  // Print handler
  const handlePrint = useCallback(() => {
    if (!marHtml) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(marHtml);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      onPrint?.();
    }
  }, [marHtml, onPrint]);

  // Download HTML handler
  const handleDownload = useCallback(() => {
    if (!marHtml) return;

    const blob = new Blob([marHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `MAR-${currentDate}-${shift}.html`;
    link.click();
    URL.revokeObjectURL(url);
  }, [marHtml, currentDate, shift]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={500} size="lg">
            Medication Administration Record
          </Text>
          {isOffline && (
            <Badge color="orange" leftSection={<IconCloudOff size={12} />}>
              Offline
            </Badge>
          )}
        </Group>

        <Group>
          <Select
            label="Shift"
            value={shift}
            onChange={(value) => setShift((value as MarShift) ?? 'AM')}
            data={[
              { value: 'AM', label: 'Morning (6am - 2pm)' },
              { value: 'PM', label: 'Afternoon (2pm - 10pm)' },
              { value: 'NOC', label: 'Night (10pm - 6am)' },
            ]}
            style={{ flex: 1, maxWidth: 200 }}
          />

          <Group gap="xs" style={{ marginTop: 24 }}>
            <Tooltip label="Generate MAR for this shift">
              <Button
                leftSection={<IconRefresh size={16} />}
                onClick={handleGenerateMar}
                loading={generating}
                disabled={isOffline}
                variant="light"
              >
                Generate
              </Button>
            </Tooltip>

            <Tooltip label="Preview MAR">
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={() => setPreviewOpen(true)}
                disabled={!marHtml}
                variant="light"
              >
                Preview
              </Button>
            </Tooltip>

            <Tooltip label="Print MAR">
              <Button
                leftSection={<IconPrinter size={16} />}
                onClick={handlePrint}
                disabled={!marHtml}
              >
                Print
              </Button>
            </Tooltip>

            <Tooltip label="Download HTML">
              <ActionIcon
                variant="subtle"
                onClick={handleDownload}
                disabled={!marHtml}
              >
                <IconDownload size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {cacheStatus === 'cached' && (
          <Alert color="green" icon={<IconCheck size={16} />}>
            MAR cached and ready for offline printing
          </Alert>
        )}

        {isOffline && cacheStatus === 'none' && (
          <Alert color="orange" icon={<IconAlertCircle size={16} />}>
            No cached MAR available. Connect to internet to generate.
          </Alert>
        )}

        {/* Preview Modal */}
        <Modal
          opened={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title="MAR Preview"
          size="xl"
          fullScreen
        >
          <ScrollArea style={{ height: 'calc(100vh - 100px)' }}>
            {marHtml && (
              <div
                dangerouslySetInnerHTML={{ __html: marHtml }}
                style={{ backgroundColor: 'white', padding: 16 }}
              />
            )}
          </ScrollArea>
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <Button leftSection={<IconPrinter size={16} />} onClick={handlePrint}>
              Print
            </Button>
          </Group>
        </Modal>

        <LoadingOverlay visible={generating} />
      </Stack>
    </Paper>
  );
}

// ============================================================================
// Helper functions
// ============================================================================

function getCurrentShift(): MarShift {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 14) return 'AM';
  if (hour >= 14 && hour < 22) return 'PM';
  return 'NOC';
}

export default MarPrintPanel;
